import { Injectable, NgZone, OnDestroy, inject } from "@angular/core";
import _, { uniqWith } from "lodash";
import {
    BehaviorSubject,
    Subject,
    asapScheduler,
    firstValueFrom,
    of
} from "rxjs";
import { catchError, map, takeUntil } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager, ICommandManager } from "~app/common/services/cqs/commandManager";
import {
    EntityType,
    RuleErrorContext,
    RuleSeverityLevel,
    RuleValidationMessage,
    RuleVisibilityStrategy
} from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VirkeskopService } from "../virkeskop.service";

export enum SaveResult {
    ok = 0,
    error = 1,
    declined = 2
}

type ValidationNode = {
    entity: EntityType;
    children: ValidationNode[];
};

const validationTree: ValidationNode = {
    entity: "avtal",
    children: [
        {
            entity: "kontrakt",
            children: [
                {
                    entity: "atgard",
                    children: [
                        {
                            entity: "tradslag",
                            children: []
                        },
                        {
                            entity: "avverkningsanmalan",
                            children: []
                        }
                    ]
                },
                {
                    entity: "certifikat",
                    children: []
                },
                {
                    entity: "utskrift",
                    children: []
                },
                {
                    entity: "skogsvardsatgard",
                    children: [
                        {
                            entity: "skogsvardsatgarddetaljer",
                            children: []
                        },
                        {
                            entity: "skogsvardsatgardovrigt",
                            children: []
                        },
                        {
                            entity: "skogsvardsatgardforyngring",
                            children: []
                        }
                    ]
                }
            ]
        }
    ]
};

const findNode = (type: EntityType): ValidationNode => {
    let start: ValidationNode;
    const traverse = (items: ValidationNode[]) => {
        if (start) {
            return;
        }

        items.forEach(i => {
            if (i.entity === type) {
                start = i;
            } else {
                traverse([...i.children]);
            }
        });
    };

    traverse([validationTree]);
    return start;
};

const compareFn = (a: RuleValidationMessage, b: RuleValidationMessage) => a.messageId === b.messageId &&
    _.isEqual(a.entityGuids, b.entityGuids);

@Injectable()
export class VirkeskopValidationService implements OnDestroy {
    validation$ = new BehaviorSubject<RuleValidationMessage[]>([]);
    visibleMessages$ = new BehaviorSubject<RuleValidationMessage[]>([]);
    validateOnChange = _.debounce(this._prevalidate, 500);

    private readonly raskModalService = inject(RaskModalService);
    private readonly commandManager = inject(CommandManager);
    private readonly commandService = inject(CommandService);
    private readonly ngZone = inject(NgZone);
    private readonly virkeskopService = inject(VirkeskopService);

    private clientSideValidation: RuleValidationMessage[] = [];
    private serverSideValidation: RuleValidationMessage[] = [];
    private senasteHandelser: RuleValidationMessage[] = [];
    private readonly destroy$ = new Subject<void>();
    constructor() {
        this.validation$.pipe(
            takeUntil(this.destroy$)
        ).subscribe((messages) => this.validationChange(messages));
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Tar bort alla klientmeddelanden som tillhör angivet sammanhang
     *
     * @param context context som töms på meddelanden
     */
    clearClientSideContext(context: RuleErrorContext) {
        this.clientSideValidation = this.clientSideValidation.filter(message =>
            message.context !== context);
        const messages = [
            ...this.serverSideValidation,
            ...this.clientSideValidation,
            ...this.senasteHandelser
        ];
        this.validation$.next(messages);
    }

    clearMessagesByEntity(entityType: EntityType, entityGuids: string[]) {
        this.clientSideValidation = this.clientSideValidation.filter(message =>
            !(message.entityType === entityType && _.isEqual(message.entityGuids, entityGuids)));
        const messages = [
            ...this.serverSideValidation,
            ...this.clientSideValidation,
            ...this.senasteHandelser
        ];
        this.validation$.next(messages);
    }

    refreshSenasteHandelser(avtalId: number) {
        if (avtalId > 0) {
            this.virkeskopService.getAlerts(avtalId).subscribe(alerts => {
                this.senasteHandelser = alerts.flatMap(x => x.alerts)
                    .filter(p => p.metadata?.entityType)
                    .map(alert => ({
                        message: alert.message,
                        severity: parseInt(alert.metadata.severity, 10),
                        strategy: RuleVisibilityStrategy.alwaysShow,
                        entityGuids: [alert.metadata.entityGuid],
                        entityType: alert.metadata.entityType as EntityType,
                        messageId: alert.metadata.messageId,
                        context: RuleErrorContext.save
                    }));

                const messages = [
                    ...this.serverSideValidation,
                    ...this.clientSideValidation,
                    ...this.senasteHandelser
                ];
                this.validation$.next(messages);
            });
        } else {
            this.senasteHandelser = [];
            const messages = [
                ...this.serverSideValidation,
                ...this.clientSideValidation,
                ...this.senasteHandelser
            ];
            this.validation$.next(messages);
        }
    }

    clear() {
        this.visibleMessages$.next([]);
        this.validation$.next([]);
        this.clientSideValidation = [];
        this.serverSideValidation = [];
    }

    updateClientSideValidation(visible: boolean, message: RuleValidationMessage) {
        const index = this.clientSideValidation.findIndex(m =>
            m.messageId === message.messageId
            && _.isEqual(m.entityGuids, message.entityGuids));

        if (!visible && index < 0) {
            return;
        }

        if (!visible) {
            this.clientSideValidation.splice(index, 1);
        } else if (index >= 0) {
            this.clientSideValidation[index] = message;
        } else {
            this.clientSideValidation.push(message);
        }

        const messages = [
            ...this.serverSideValidation,
            ...this.clientSideValidation,
            ...this.senasteHandelser
        ];
        this.addUniqueMessages(messages);
    }

    /**
     * Traverserar trädstrukturen fr.om. angiven nivå (entitetstyp) och returnerar en ikon som representerar det mest allvarliga valideringsfelet
     * (om sådant finns)
     *
     * @param type entitetstyp/-nivå
     * @param guid entitetsguid
     */
    getEntitySeverity(type: EntityType, guid: string): string {
        const start = findNode(type);
        const messages = this.visibleMessages$.value;

        const entities = _.chain([
            start,
            ...start.children
        ])
            .flatMapDeep(x => [
                x,
                ...x.children
            ])
            .map(x => x.entity)
            .value();

        const severities = _.chain(messages)
            .filter(m => _.includes(entities, m.entityType)
                && m.entityGuids.includes(guid))
            .map(m => m.severity)
            .uniq()
            .value();

        if (_.includes(severities, RuleSeverityLevel.error)) {
            return "far fa-exclamation-circle error";
        } else if (_.includes(severities, RuleSeverityLevel.warning)) {
            return "far fa-exclamation-square warning";
        } else if (_.includes(severities, RuleSeverityLevel.info)) {
            return "far fa-info-circle";
        } else {
            return "";
        }
    }

    filterVisibleMessages(messageId: string[], entityType?: EntityType, entityGuid?: string, context?: RuleErrorContext) {
        const filterMessage = (message: RuleValidationMessage) =>
            (messageId?.includes(message.messageId) ?? true)
            && (ObjectUtils.isNullOrUndefined(entityType) || entityType === message.entityType)
            && (ObjectUtils.isNullOrUndefined(entityGuid) || message.entityGuids.includes(entityGuid))
            && (ObjectUtils.isNullOrUndefined(context) || (message.severity === RuleSeverityLevel.error
                && context === message.context));

        return this.visibleMessages$.pipe(map(messages =>
            messages.filter(filterMessage)));
    }

    async validateOnSave(): Promise<SaveResult> {
        const messages = await this._prevalidate(this.commandManager);
        const confirmed = await this.saveConfirm(messages);

        this.filterPreSaveMessages();

        if (messages.some(m =>
            m.severity === RuleSeverityLevel.error &&
            m.context === RuleErrorContext.save)) {
            return SaveResult.error;
        }

        if (!confirmed) {
            return SaveResult.declined;
        }

        return SaveResult.ok;
    }

    private addUniqueMessages(messages: RuleValidationMessage[]) {
        // Filtera listan på dubbletter då det alternativa klientvaliderings mönstret kolliderar en del med det tidigare
        const uniqueMessages = uniqWith(messages, compareFn);
        asapScheduler.schedule(() => {
            this.validation$.next(uniqueMessages);
        });
        return uniqueMessages;
    }

    /*
    vid validering lägger vi inte till meddelanden med strategi "post save"
    vi kan däremot ta bort "post save"-meddelanden som inte längre är aktuella
    */
    private validationChange(messages: RuleValidationMessage[]) {
        const previous = this.visibleMessages$.value;
        const showMessage = (message: RuleValidationMessage) => {
            switch (message.strategy) {
                case RuleVisibilityStrategy.postSave:
                    return previous.some(x =>
                        x.messageId === message.messageId
                        && _.isEqual(x.entityGuids, message.entityGuids));
                case RuleVisibilityStrategy.confirm:
                case RuleVisibilityStrategy.alwaysShow:
                case RuleVisibilityStrategy.preSave:
                default:
                    return true;
            }
        };

        const filterMessages = messages.filter(showMessage);
        this.visibleMessages$.next(filterMessages);
    }

    private async saveConfirm(messages: RuleValidationMessage[]): Promise<boolean> {
        const showMessage = (message: RuleValidationMessage) => (
            message.strategy === RuleVisibilityStrategy.confirm);

        messages = messages.filter(showMessage);

        if (messages.length === 0) {
            return true;
        }

        const modalText = messages
            .map(x => `${x.message}`)
            .join("<br>");
        const settings = new ModalSettings("Observera", modalText);
        settings.yesCaption = "Spara";
        settings.noCaption = "Avbryt";
        settings.centered = true;
        settings.contentIsMarkup = true;
        settings.windowClass = "top-priority";
        const confirm$ = this.raskModalService.confirm(settings)
            .pipe(
                catchError(err => of(false)),
                map(result => result === ConfirmDialogResponse.save)
            );

        return firstValueFrom(confirm$);
    }

    /*
    när save emittas visas alla aktuella meddelanden, förutom dem med strategi "pre save"
    */
    private filterPreSaveMessages() {
        const filterPreSave = (message: RuleValidationMessage) => (
            message.strategy !== RuleVisibilityStrategy.preSave);

        this.serverSideValidation = this.serverSideValidation.filter(filterPreSave);
        this.clientSideValidation = this.clientSideValidation.filter(filterPreSave);

        this.visibleMessages$.next([
            ...this.serverSideValidation,
            ...this.clientSideValidation
        ]);
    }

    private async _prevalidate(commandManager: ICommandManager) {
        return this.ngZone.runOutsideAngular(async () => {
            this.serverSideValidation = await this.commandService.prevalidateCommands(commandManager);
            const messages = [
                ...this.serverSideValidation,
                ...this.clientSideValidation,
                ...this.senasteHandelser
            ];
            return this.addUniqueMessages(messages);
        });
    }
}
